/**
 * Financial Forecast Template Form
 * Revenue & expense projections with scenarios
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const FinancialForecastForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '',
    forecast_name: '',
    fiscal_year: new Date().getFullYear().toString(),
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    currency: 'USD',
    revenue_items: [{
      category: 'Revenue', item: '', month_1: 0, month_2: 0, month_3: 0, month_4: 0, month_5: 0, month_6: 0,
      month_7: 0, month_8: 0, month_9: 0, month_10: 0, month_11: 0, month_12: 0, annual_total: 0, growth_rate: '', notes: ''
    }],
    expense_items: [{
      category: 'Operating Expenses', item: '', month_1: 0, month_2: 0, month_3: 0, month_4: 0, month_5: 0, month_6: 0,
      month_7: 0, month_8: 0, month_9: 0, month_10: 0, month_11: 0, month_12: 0, annual_total: 0, growth_rate: '', notes: ''
    }],
    assumptions: [''],
    scenarios: [],
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    forecast_name: 'FY2025 Financial Forecast',
    fiscal_year: '2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'CFO',
    currency: 'USD',
    revenue_items: [
      { category: 'Revenue', item: 'SaaS Subscriptions', month_1: 250000, month_2: 265000, month_3: 280000, month_4: 295000, month_5: 310000, month_6: 325000, month_7: 340000, month_8: 355000, month_9: 370000, month_10: 385000, month_11: 400000, month_12: 420000, annual_total: 3995000, growth_rate: '5% MoM', notes: 'Core product revenue' },
      { category: 'Revenue', item: 'Professional Services', month_1: 50000, month_2: 55000, month_3: 60000, month_4: 45000, month_5: 70000, month_6: 65000, month_7: 55000, month_8: 60000, month_9: 75000, month_10: 80000, month_11: 70000, month_12: 85000, annual_total: 770000, growth_rate: 'Variable', notes: 'Implementation & consulting' },
      { category: 'Revenue', item: 'Enterprise Licenses', month_1: 0, month_2: 100000, month_3: 0, month_4: 150000, month_5: 0, month_6: 200000, month_7: 0, month_8: 0, month_9: 250000, month_10: 0, month_11: 0, month_12: 300000, annual_total: 1000000, growth_rate: 'Deal-based', notes: 'Annual enterprise deals' }
    ],
    expense_items: [
      { category: 'Personnel', item: 'Salaries & Benefits', month_1: 180000, month_2: 180000, month_3: 185000, month_4: 185000, month_5: 190000, month_6: 190000, month_7: 195000, month_8: 195000, month_9: 200000, month_10: 200000, month_11: 205000, month_12: 205000, annual_total: 2310000, growth_rate: '3% Quarterly', notes: 'Growing team' },
      { category: 'Infrastructure', item: 'Cloud Hosting (AWS)', month_1: 35000, month_2: 36000, month_3: 37000, month_4: 38000, month_5: 39000, month_6: 40000, month_7: 41000, month_8: 42000, month_9: 43000, month_10: 44000, month_11: 45000, month_12: 46000, annual_total: 486000, growth_rate: '3% MoM', notes: 'Scaling with users' },
      { category: 'Marketing', item: 'Digital Marketing', month_1: 25000, month_2: 30000, month_3: 35000, month_4: 40000, month_5: 45000, month_6: 50000, month_7: 45000, month_8: 40000, month_9: 50000, month_10: 55000, month_11: 60000, month_12: 65000, annual_total: 540000, growth_rate: 'Campaign-based', notes: 'Q2 & Q4 heavy' },
      { category: 'Operations', item: 'Office & Admin', month_1: 15000, month_2: 15000, month_3: 15000, month_4: 15000, month_5: 15000, month_6: 15000, month_7: 15000, month_8: 15000, month_9: 15000, month_10: 15000, month_11: 15000, month_12: 15000, annual_total: 180000, growth_rate: 'Flat', notes: 'Fixed costs' }
    ],
    assumptions: [
      'Customer acquisition cost (CAC) remains at $500 average',
      'Churn rate maintained at 2% monthly',
      'No major pricing changes in FY2025',
      'Hiring plan: 15 new employees across the year',
      'No significant capital expenditures planned'
    ],
    scenarios: [],
    rai_assignments: [
      { task: 'Monthly forecast variance analysis', responsible: 'Finance Manager', accountable: 'CFO', informed: 'CEO, Board', due_date: '2025-01-10', status: 'Pending' }
    ],
    followups: [
      { item: 'Update Q1 actuals', owner: 'Finance Manager', due_date: '2025-04-05', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Financial Forecast Draft";
      const payload = {
        name: draftName,
        template_type: "Financial Forecast",
        template_id: "financial-forecast",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "financial-forecast", "Financial Forecast", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("financial-forecast", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/financial-forecast`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Financial_Forecast_${formData.company_name.replace(/\s+/g, '_') || 'Document'}_${formData.fiscal_year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Financial Forecast",
        formData,
        settings: activeSettings || {},
        templateType: "Financial Forecast",
        templateId: "financial-forecast",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addRevenueItem = () => {
    setFormData({
      ...formData,
      revenue_items: [...formData.revenue_items, {
        category: 'Revenue', item: '', month_1: 0, month_2: 0, month_3: 0, month_4: 0, month_5: 0, month_6: 0,
        month_7: 0, month_8: 0, month_9: 0, month_10: 0, month_11: 0, month_12: 0, annual_total: 0, growth_rate: '', notes: ''
      }]
    });
  };

  const addExpenseItem = () => {
    setFormData({
      ...formData,
      expense_items: [...formData.expense_items, {
        category: 'Operating Expenses', item: '', month_1: 0, month_2: 0, month_3: 0, month_4: 0, month_5: 0, month_6: 0,
        month_7: 0, month_8: 0, month_9: 0, month_10: 0, month_11: 0, month_12: 0, annual_total: 0, growth_rate: '', notes: ''
      }]
    });
  };

  const updateItem = (type, index, field, value) => {
    const items = [...formData[type]];
    items[index] = { ...items[index], [field]: value };
    // Auto-calculate annual total
    if (field.startsWith('month_')) {
      let total = 0;
      for (let i = 1; i <= 12; i++) {
        total += parseFloat(items[index][`month_${i}`]) || 0;
      }
      items[index].annual_total = total;
    }
    setFormData({ ...formData, [type]: items });
  };

  const removeItem = (type, index) => {
    setFormData({ ...formData, [type]: formData[type].filter((_, i) => i !== index) });
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'JPY'];
  const revenueCategories = ['Revenue', 'Recurring Revenue', 'One-time Revenue', 'Services', 'Other Income'];
  const expenseCategories = ['Personnel', 'Infrastructure', 'Marketing', 'Sales', 'Operations', 'R&D', 'G&A', 'Other'];

  const renderLineItems = (type, items, categories, addFn) => (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="p-3 bg-white rounded border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={item.category} onValueChange={(v) => updateItem(type, idx, 'category', v)}>
                <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Line Item</Label>
              <Input value={item.item} onChange={(e) => updateItem(type, idx, 'item', e.target.value)} placeholder="Item description" className="text-sm" />
            </div>
            <div className="flex items-end justify-end">
              <Button variant="ghost" size="icon" onClick={() => removeItem(type, idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          </div>
          
          <div className="grid grid-cols-6 md:grid-cols-13 gap-1">
            {months.map((month, mIdx) => (
              <div key={mIdx}>
                <Label className="text-[10px] text-gray-500">{month}</Label>
                <Input
                  type="number"
                  value={item[`month_${mIdx + 1}`]}
                  onChange={(e) => updateItem(type, idx, `month_${mIdx + 1}`, parseFloat(e.target.value) || 0)}
                  className="text-xs h-8 px-1"
                />
              </div>
            ))}
            <div className="col-span-6 md:col-span-1 bg-gray-100 rounded p-1">
              <Label className="text-[10px] text-gray-500 font-bold">Total</Label>
              <div className="text-xs font-medium">${item.annual_total?.toLocaleString()}</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Label className="text-xs">Growth Rate</Label>
              <Input value={item.growth_rate} onChange={(e) => updateItem(type, idx, 'growth_rate', e.target.value)} placeholder="e.g., 5% MoM" className="text-sm" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={item.notes} onChange={(e) => updateItem(type, idx, 'notes', e.target.value)} placeholder="Notes" className="text-sm" />
            </div>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addFn}><Plus className="h-4 w-4 mr-1" /> Add Line Item</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Financial Forecast</h1>
                <p className="text-sm text-gray-500">12-month revenue & expense projections</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Forecast Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="col-span-2"><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company name" /></div>
              <div className="col-span-2"><Label>Forecast Name</Label><Input value={formData.forecast_name} onChange={(e) => setFormData({...formData, forecast_name: e.target.value})} placeholder="FY2025 Forecast" /></div>
              <div><Label>Fiscal Year</Label><Input value={formData.fiscal_year} onChange={(e) => setFormData({...formData, fiscal_year: e.target.value})} placeholder="2025" /></div>
              <div><Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="CFO" /></div>
            </div>

            <Separator />

            <Tabs defaultValue="revenue">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="revenue">Revenue ({formData.revenue_items.length})</TabsTrigger>
                <TabsTrigger value="expenses">Expenses ({formData.expense_items.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="revenue" className="mt-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <Label className="font-semibold text-green-800 mb-3 block">Revenue Line Items</Label>
                  {renderLineItems('revenue_items', formData.revenue_items, revenueCategories, addRevenueItem)}
                </div>
              </TabsContent>
              <TabsContent value="expenses" className="mt-4">
                <div className="bg-red-50 rounded-lg p-4">
                  <Label className="font-semibold text-red-800 mb-3 block">Expense Line Items</Label>
                  {renderLineItems('expense_items', formData.expense_items, expenseCategories, addExpenseItem)}
                </div>
              </TabsContent>
            </Tabs>

            <Separator />

            <div>
              <Label className="font-semibold">Key Assumptions</Label>
              {formData.assumptions.map((assumption, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <Input value={assumption} onChange={(e) => {
                    const assumptions = [...formData.assumptions];
                    assumptions[idx] = e.target.value;
                    setFormData({...formData, assumptions});
                  }} placeholder="Assumption" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, assumptions: formData.assumptions.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, assumptions: [...formData.assumptions, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add Assumption</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default FinancialForecastForm;
